import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import { useAdminNotifications } from '@/context/AdminNotificationContext';
import { motion } from 'framer-motion';
import { 
  Settings,
  Save,
  Percent,
  DollarSign,
  Wallet,
  AlertCircle,
  Bell,
  BellOff,
  Volume2,
  VolumeX,
  UserPlus,
  Copy,
  ArrowDownToLine,
  ArrowUpFromLine,
  Wifi,
  WifiOff,
  Mail,
  MailX,
  Smartphone,
  SmartphoneNfc,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [testingPush, setTestingPush] = useState(false);

  // Notification settings from context
  const { 
    notificationSettings, 
    updateSettings: updateNotificationSettings, 
    permissionGranted, 
    requestPermission,
    wsConnected,
    pushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    testPushNotification
  } = useAdminNotifications();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminAPI.getSettings();
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = async (key, value) => {
    setSavingNotifications(true);
    await updateNotificationSettings({ [key]: value });
    setSavingNotifications(false);
  };

  const handleRequestPermission = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success('Notification permission granted!');
    } else {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-hotpink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-6"
      data-testid="admin-settings-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white">Platform Settings</h1>
          <p className="text-white/60 mt-1">Configure platform parameters and notifications</p>
        </div>
        <Button 
          onClick={handleSave} 
          className="btn-primary"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </motion.div>

      {/* Real-Time Notification Settings */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-outfit font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            Real-Time Notifications
          </h2>
          {wsConnected ? (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <Wifi className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-medium">Connected</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <WifiOff className="w-3 h-3 text-yellow-400" />
              <span className="text-yellow-400 text-xs font-medium">Reconnecting</span>
            </div>
          )}
        </div>
        
        <p className="text-white/60 text-sm mb-6">
          Receive instant popup notifications for user activities. Notifications appear even when you're on other browser tabs.
        </p>

        {/* Browser Permission Status */}
        {!permissionGranted && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-400/10 border border-yellow-400/30 mb-6">
            <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-yellow-400 font-medium text-sm">Browser Notifications Disabled</p>
              <p className="text-yellow-400/70 text-xs mt-1">
                Enable browser notifications to receive alerts outside this tab.
              </p>
              <Button 
                size="sm" 
                className="mt-3 bg-yellow-400 text-black hover:bg-yellow-500"
                onClick={handleRequestPermission}
                data-testid="enable-notifications-btn"
              >
                <Bell className="w-3 h-3 mr-1" />
                Enable Notifications
              </Button>
            </div>
          </div>
        )}

        {/* Master Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 mb-4">
          <div className="flex items-center gap-3">
            {notificationSettings.enabled ? (
              <Bell className="w-5 h-5 text-cyan-400" />
            ) : (
              <BellOff className="w-5 h-5 text-white/40" />
            )}
            <div>
              <p className="text-white font-medium">Enable All Notifications</p>
              <p className="text-white/50 text-xs">Master toggle for all notification types</p>
            </div>
          </div>
          <Switch
            checked={notificationSettings.enabled}
            onCheckedChange={(checked) => handleNotificationToggle('enabled', checked)}
            disabled={savingNotifications}
            data-testid="notifications-master-toggle"
          />
        </div>

        {/* Sound Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 mb-6">
          <div className="flex items-center gap-3">
            {notificationSettings.sound_enabled ? (
              <Volume2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <VolumeX className="w-5 h-5 text-white/40" />
            )}
            <div>
              <p className="text-white font-medium">Notification Sound</p>
              <p className="text-white/50 text-xs">Play sound when notifications arrive</p>
            </div>
          </div>
          <Switch
            checked={notificationSettings.sound_enabled}
            onCheckedChange={(checked) => handleNotificationToggle('sound_enabled', checked)}
            disabled={savingNotifications || !notificationSettings.enabled}
            data-testid="notifications-sound-toggle"
          />
        </div>

        {/* Event Type Toggles */}
        <div className="space-y-3">
          <p className="text-white/60 text-sm font-medium mb-3">Notification Types</p>
          
          {/* New User Signup */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">New User Signups</p>
                <p className="text-white/40 text-xs">When a new user registers</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.new_user_signup}
              onCheckedChange={(checked) => handleNotificationToggle('new_user_signup', checked)}
              disabled={savingNotifications || !notificationSettings.enabled}
              data-testid="toggle-new-user-signup"
            />
          </div>

          {/* Wallet Address Copied */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-400/10 flex items-center justify-center">
                <Copy className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Wallet Address Copied</p>
                <p className="text-white/40 text-xs">When user copies deposit address</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.wallet_address_copied}
              onCheckedChange={(checked) => handleNotificationToggle('wallet_address_copied', checked)}
              disabled={savingNotifications || !notificationSettings.enabled}
              data-testid="toggle-wallet-copied"
            />
          </div>

          {/* Deposit Submitted */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 flex items-center justify-center">
                <ArrowDownToLine className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Deposit Submitted</p>
                <p className="text-white/40 text-xs">When user submits a deposit request</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.deposit_submitted}
              onCheckedChange={(checked) => handleNotificationToggle('deposit_submitted', checked)}
              disabled={savingNotifications || !notificationSettings.enabled}
              data-testid="toggle-deposit-submitted"
            />
          </div>

          {/* Withdrawal Requested */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-hotpink-400/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-4 h-4 text-hotpink-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Withdrawal Requested</p>
                <p className="text-white/40 text-xs">When user requests a withdrawal</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.withdrawal_requested}
              onCheckedChange={(checked) => handleNotificationToggle('withdrawal_requested', checked)}
              disabled={savingNotifications || !notificationSettings.enabled}
              data-testid="toggle-withdrawal-requested"
            />
          </div>
        </div>
      </motion.div>

      {/* Email Notifications Section */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-outfit font-semibold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-400" />
            Email Notifications
          </h2>
        </div>
        
        <p className="text-white/60 text-sm mb-6">
          Receive email alerts for important events. Perfect for staying informed even when you're away from the dashboard.
        </p>

        {/* Email Master Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 mb-6">
          <div className="flex items-center gap-3">
            {notificationSettings.email_enabled ? (
              <Mail className="w-5 h-5 text-purple-400" />
            ) : (
              <MailX className="w-5 h-5 text-white/40" />
            )}
            <div>
              <p className="text-white font-medium">Enable Email Notifications</p>
              <p className="text-white/50 text-xs">Receive email alerts for selected events</p>
            </div>
          </div>
          <Switch
            checked={notificationSettings.email_enabled || false}
            onCheckedChange={(checked) => handleNotificationToggle('email_enabled', checked)}
            disabled={savingNotifications}
            data-testid="email-notifications-toggle"
          />
        </div>

        {/* Email Event Types */}
        <div className="space-y-3">
          <p className="text-white/60 text-sm font-medium mb-3">Email Alert Types</p>
          
          {/* Email: New User Signup */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">New User Signups</p>
                <p className="text-white/40 text-xs">Email when a new user registers</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.email_new_user_signup || false}
              onCheckedChange={(checked) => handleNotificationToggle('email_new_user_signup', checked)}
              disabled={savingNotifications || !notificationSettings.email_enabled}
              data-testid="email-toggle-new-user-signup"
            />
          </div>

          {/* Email: Wallet Address Copied */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-400/10 flex items-center justify-center">
                <Copy className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Wallet Address Copied</p>
                <p className="text-white/40 text-xs">Email when user copies deposit address (disabled by default)</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.email_wallet_address_copied || false}
              onCheckedChange={(checked) => handleNotificationToggle('email_wallet_address_copied', checked)}
              disabled={savingNotifications || !notificationSettings.email_enabled}
              data-testid="email-toggle-wallet-copied"
            />
          </div>

          {/* Email: Deposit Submitted */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 flex items-center justify-center">
                <ArrowDownToLine className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Deposit Submitted</p>
                <p className="text-white/40 text-xs">Email when user submits a deposit request</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.email_deposit_submitted || false}
              onCheckedChange={(checked) => handleNotificationToggle('email_deposit_submitted', checked)}
              disabled={savingNotifications || !notificationSettings.email_enabled}
              data-testid="email-toggle-deposit-submitted"
            />
          </div>

          {/* Email: Withdrawal Requested */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-hotpink-400/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-4 h-4 text-hotpink-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Withdrawal Requested</p>
                <p className="text-white/40 text-xs">Email when user requests a withdrawal</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.email_withdrawal_requested || false}
              onCheckedChange={(checked) => handleNotificationToggle('email_withdrawal_requested', checked)}
              disabled={savingNotifications || !notificationSettings.email_enabled}
              data-testid="email-toggle-withdrawal-requested"
            />
          </div>
        </div>

        {/* Email Info */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-purple-400/10 border border-purple-400/30 mt-6">
          <AlertCircle className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
          <p className="text-sm text-purple-400">
            Email notifications are sent to the admin email configured in environment variables. This ensures you never miss important platform activity.
          </p>
        </div>
      </motion.div>

      {/* Mobile Push Notifications Section */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-outfit font-semibold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-emerald-400" />
            Mobile Push Notifications
          </h2>
          {pushSubscribed && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <SmartphoneNfc className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-medium">Subscribed</span>
            </div>
          )}
        </div>
        
        <p className="text-white/60 text-sm mb-6">
          Receive push notifications on your mobile device even when the browser is closed. Works on Android and iOS (when added to home screen).
        </p>

        {/* PWA Installation Note */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-cyan-400/10 border border-cyan-400/30 mb-6">
          <AlertCircle className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
          <div className="text-sm text-cyan-400">
            <p className="font-medium mb-1">For best mobile experience:</p>
            <p className="text-cyan-400/80">Add this app to your home screen (Share → Add to Home Screen) for app-like push notifications.</p>
          </div>
        </div>

        {/* Push Subscription Controls */}
        <div className="flex flex-wrap gap-3 mb-6">
          {!pushSubscribed ? (
            <Button
              onClick={async () => {
                setSavingNotifications(true);
                await subscribeToPush();
                setSavingNotifications(false);
              }}
              disabled={savingNotifications}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="enable-push-btn"
            >
              <SmartphoneNfc className="w-4 h-4 mr-2" />
              Enable Push Notifications
            </Button>
          ) : (
            <>
              <Button
                onClick={async () => {
                  setSavingNotifications(true);
                  await unsubscribeFromPush();
                  setSavingNotifications(false);
                }}
                disabled={savingNotifications}
                variant="outline"
                className="border-red-400/50 text-red-400 hover:bg-red-400/10"
                data-testid="disable-push-btn"
              >
                Disable Push
              </Button>
              <Button
                onClick={async () => {
                  setTestingPush(true);
                  await testPushNotification();
                  setTestingPush(false);
                }}
                disabled={testingPush}
                variant="outline"
                className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
                data-testid="test-push-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {testingPush ? 'Sending...' : 'Send Test'}
              </Button>
            </>
          )}
        </div>

        {/* Push Master Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className={`w-5 h-5 ${notificationSettings.push_enabled ? 'text-emerald-400' : 'text-white/40'}`} />
            <div>
              <p className="text-white font-medium">Enable Push Alert Types</p>
              <p className="text-white/50 text-xs">Control which events trigger push notifications</p>
            </div>
          </div>
          <Switch
            checked={notificationSettings.push_enabled || false}
            onCheckedChange={(checked) => handleNotificationToggle('push_enabled', checked)}
            disabled={savingNotifications || !pushSubscribed}
            data-testid="push-notifications-toggle"
          />
        </div>

        {/* Push Event Types */}
        <div className="space-y-3">
          <p className="text-white/60 text-sm font-medium mb-3">Push Alert Types</p>
          
          {/* Push: New User Signup */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">New User Signups</p>
                <p className="text-white/40 text-xs">Push when a new user registers</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.push_new_user_signup || false}
              onCheckedChange={(checked) => handleNotificationToggle('push_new_user_signup', checked)}
              disabled={savingNotifications || !notificationSettings.push_enabled || !pushSubscribed}
              data-testid="push-toggle-new-user-signup"
            />
          </div>

          {/* Push: Deposit Submitted */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 flex items-center justify-center">
                <ArrowDownToLine className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Deposit Submitted</p>
                <p className="text-white/40 text-xs">Push when user submits a deposit</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.push_deposit_submitted || false}
              onCheckedChange={(checked) => handleNotificationToggle('push_deposit_submitted', checked)}
              disabled={savingNotifications || !notificationSettings.push_enabled || !pushSubscribed}
              data-testid="push-toggle-deposit-submitted"
            />
          </div>

          {/* Push: Withdrawal Requested */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-hotpink-400/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-4 h-4 text-hotpink-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Withdrawal Requested</p>
                <p className="text-white/40 text-xs">Push when user requests a withdrawal</p>
              </div>
            </div>
            <Switch
              checked={notificationSettings.push_withdrawal_requested || false}
              onCheckedChange={(checked) => handleNotificationToggle('push_withdrawal_requested', checked)}
              disabled={savingNotifications || !notificationSettings.push_enabled || !pushSubscribed}
              data-testid="push-toggle-withdrawal-requested"
            />
          </div>
        </div>
      </motion.div>

      {/* Profit Settings */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <h2 className="text-xl font-outfit font-semibold text-white mb-4 flex items-center gap-2">
          <Percent className="w-5 h-5 text-cyan-400" />
          Profit Settings
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white/80">Daily Profit Rate (%)</Label>
            <Input
              type="number"
              step="0.1"
              value={settings?.daily_profit_rate || ''}
              onChange={(e) => setSettings({ ...settings, daily_profit_rate: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="daily-rate-input"
            />
            <p className="text-xs text-white/50">
              This rate is applied to all active investments daily
            </p>
          </div>
        </div>
      </motion.div>

      {/* Transaction Limits */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <h2 className="text-xl font-outfit font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          Transaction Limits
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label className="text-white/80">Minimum Deposit ($)</Label>
            <Input
              type="number"
              value={settings?.min_deposit || ''}
              onChange={(e) => setSettings({ ...settings, min_deposit: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="min-deposit-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Maximum Deposit ($)</Label>
            <Input
              type="number"
              value={settings?.max_deposit || ''}
              onChange={(e) => setSettings({ ...settings, max_deposit: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="max-deposit-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Minimum Withdrawal ($)</Label>
            <Input
              type="number"
              value={settings?.min_withdrawal || ''}
              onChange={(e) => setSettings({ ...settings, min_withdrawal: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="min-withdrawal-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Maximum Withdrawal ($)</Label>
            <Input
              type="number"
              value={settings?.max_withdrawal || ''}
              onChange={(e) => setSettings({ ...settings, max_withdrawal: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="max-withdrawal-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Withdrawal Fee ($)</Label>
            <Input
              type="number"
              step="0.1"
              value={settings?.withdrawal_fee || ''}
              onChange={(e) => setSettings({ ...settings, withdrawal_fee: parseFloat(e.target.value) })}
              className="input-field"
              data-testid="withdrawal-fee-input"
            />
          </div>
        </div>
      </motion.div>

      {/* Wallet Addresses */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <h2 className="text-xl font-outfit font-semibold text-white mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-yellow-400" />
          Platform Wallet Addresses
        </h2>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-400/10 border border-yellow-400/30 mb-6">
          <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-400">
            These are the wallet addresses where users will send their deposits. Make sure they are correct before saving.
          </p>
        </div>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label className="text-white/80">Bitcoin (BTC) Wallet</Label>
            <Input
              type="text"
              value={settings?.btc_wallet || ''}
              onChange={(e) => setSettings({ ...settings, btc_wallet: e.target.value })}
              className="input-field font-mono"
              placeholder="bc1q..."
              data-testid="btc-wallet-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Ethereum (ETH) Wallet</Label>
            <Input
              type="text"
              value={settings?.eth_wallet || ''}
              onChange={(e) => setSettings({ ...settings, eth_wallet: e.target.value })}
              className="input-field font-mono"
              placeholder="0x..."
              data-testid="eth-wallet-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Tether (USDT) Wallet (TRC20)</Label>
            <Input
              type="text"
              value={settings?.usdt_wallet || ''}
              onChange={(e) => setSettings({ ...settings, usdt_wallet: e.target.value })}
              className="input-field font-mono"
              placeholder="T..."
              data-testid="usdt-wallet-input"
            />
          </div>
        </div>
      </motion.div>

      {/* Save Button (Mobile) */}
      <motion.div variants={fadeInUp} className="lg:hidden">
        <Button 
          onClick={handleSave} 
          className="btn-primary w-full"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </motion.div>
    </motion.div>
  );
}
